import type { BrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import type { OrgId, UserId } from '../shared/ids';
import type { Unsubscribe } from '../shared/realtime-events';
import type { Segment } from './segment';
import type { SegmentId } from './segment-id';

export type { Unsubscribe };

export type SegmentEvent =
  | { readonly kind: 'INSERT'; readonly segment: Segment }
  | { readonly kind: 'UPDATE'; readonly segment: Segment }
  | { readonly kind: 'DELETE'; readonly id: SegmentId };

export type SegmentCreateInput = {
  readonly alias: string;
  readonly orgId: OrgId | null;
  readonly items?: {
    readonly skus?: readonly EAN[];
    readonly brands?: readonly BrandSlug[];
  };
};

export abstract class SegmentRepository {
  abstract getById(id: SegmentId): Promise<Segment | null>;
  /**
   * Every segment `userId` may see: own, org-owned and shared rows, each
   * carrying its provenance. The server decides membership; the id names whose list this is and must be
   * the signed-in user.
   */
  abstract listForUser(userId: UserId): Promise<readonly Segment[]>;

  /** Creates a segment owned by `userId`, who must be the signed-in user. */
  abstract create(userId: UserId, input: SegmentCreateInput): Promise<Segment>;
  abstract rename(id: SegmentId, alias: string): Promise<Segment>;
  abstract share(id: SegmentId): Promise<Segment>;
  abstract unshare(id: SegmentId): Promise<Segment>;
  abstract addSkus(id: SegmentId, eans: readonly EAN[]): Promise<Segment>;
  abstract removeSkus(id: SegmentId, eans: readonly EAN[]): Promise<Segment>;
  /** Who pinned each EAN. Attribution of manual edits, not membership. */
  abstract attributionFor(id: SegmentId): Promise<ReadonlyMap<EAN, UserId>>;
  /**
   * Wholesale replacement of a segment's contents (used by the populate flow):
   * sets the full sku/brand set and resets per-EAN attribution. Optionally renames
   * in the same write so populate stays atomic.
   */
  abstract replaceItems(
    id: SegmentId,
    params: {
      readonly alias?: string;
      readonly skus: readonly EAN[];
      readonly brands?: readonly BrandSlug[];
    }
  ): Promise<Segment>;
  abstract delete(id: SegmentId): Promise<void>;

  /**
   * Live changes to every segment `userId` may see, on one `portal_comparison` channel with no
   * filter. The database's `comparison_read` policy decides, per subscriber, which INSERT and
   * UPDATE events arrive, for every org the user belongs to. DELETE events are not
   * policy-filtered and carry the primary key only. An UPDATE whose new row fails the policy is
   * withheld by Realtime (no event), so eviction of such a row waits for a reload.
   */
  abstract subscribeOwnership(userId: UserId, callback: (event: SegmentEvent) => void): Unsubscribe;
}
